import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client';
import { SEARCH_CLIENTS } from '../graphql/queries';
import AddClientModalFull from './AddClientModalFull';
import { useCompany } from '../context/CompanyContext';

const normalizeClient = (raw) => {
  if (!raw) return null;
  const client = raw.client || raw;
  if (!client?.id) return null;
  return client;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightMatch = (text, query) => {
  if (!text) return null;
  const term = (query || '').trim();
  if (!term) return text;

  const parts = String(text).split(new RegExp(`(${escapeRegExp(term)})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <Box
        component="mark"
        key={`${part}-${index}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.35),
          color: 'text.primary',
          px: 0.25,
          borderRadius: 0.5,
          fontWeight: 700,
        }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );
};

const ClientOption = ({ client, query }) => {
  const isPerson = client.clientType === 'B2C' || client.isIndividual;
  const details = [
    client.eik ? `ЕИК ${client.eik}` : null,
    client.vatNumber ? `ДДС ${client.vatNumber}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%', py: 0.25 }}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          mt: 0.25,
          bgcolor: (theme) => alpha(theme.palette.primary.main, isPerson ? 0.12 : 0.18),
          color: 'primary.main',
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {isPerson ? <PersonIcon fontSize="small" /> : (client.name?.[0] || <BusinessIcon fontSize="small" />)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
          {highlightMatch(client.name, query)}
        </Typography>
        {details && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {highlightMatch(details, query)}
          </Typography>
        )}
        {client.address && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {highlightMatch(client.address, query)}
          </Typography>
        )}
        {client.contactPerson && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            МОЛ: {highlightMatch(client.contactPerson, query)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const ClientPicker = ({
  value,
  onChange,
  onClientAdded,
  error = false,
  helperText = '',
  disabled = false,
  required = true,
}) => {
  const { activeCompanyId } = useCompany();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const requestIdRef = useRef(0);

  const [searchClients, { loading }] = useLazyQuery(SEARCH_CLIENTS, {
    fetchPolicy: 'network-only',
  });

  const runSearch = useCallback(async (term) => {
    if (!activeCompanyId) {
      setOptions([]);
      return;
    }

    const requestId = ++requestIdRef.current;
    try {
      const { data } = await searchClients({
        variables: {
          companyId: activeCompanyId,
          searchTerm: (term || '').trim(),
        },
      });
      if (requestId === requestIdRef.current) {
        setOptions(data?.searchClients || []);
      }
    } catch (err) {
      console.error('Error searching clients:', err);
      if (requestId === requestIdRef.current) {
        setOptions([]);
      }
    }
  }, [activeCompanyId, searchClients]);

  useEffect(() => {
    if (!open) {
      setInputValue(value?.name || '');
    }
  }, [value, open]);

  const searchQuery = inputValue.trim() === (value?.name || '').trim()
    ? ''
    : inputValue;

  useEffect(() => {
    if (!open) return undefined;

    const timer = setTimeout(() => {
      runSearch(searchQuery);
    }, searchQuery.trim() ? 250 : 0);

    return () => clearTimeout(timer);
  }, [searchQuery, open, runSearch]);

  const mergedOptions = useMemo(() => {
    if (value?.id && !options.some((option) => option.id === value.id)) {
      return [value, ...options];
    }
    return options;
  }, [options, value]);

  const handleAdded = (rawClient) => {
    setIsAddClientModalOpen(false);
    const client = normalizeClient(rawClient);
    if (client) {
      onChange(client);
      setInputValue(client.name || '');
    }
    if (onClientAdded) {
      onClientAdded(client);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
        <Autocomplete
          fullWidth
          openOnFocus
          autoHighlight
          blurOnSelect
          disabled={disabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={mergedOptions}
          value={value || null}
          inputValue={inputValue}
          loading={loading}
          selectOnFocus
          handleHomeEndKeys
          filterOptions={(opts, state) => {
            const query = (state.inputValue || '').trim().toLowerCase();
            const selectedName = (value?.name || '').trim().toLowerCase();
            if (!query || query === selectedName) return opts;
            return opts.filter((option) =>
              [option.name, option.nameEn, option.eik, option.vatNumber, option.address, option.contactPerson, option.email]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(query))
            );
          }}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          getOptionLabel={(option) => option?.name || ''}
          noOptionsText={
            loading
              ? 'Търсене...'
              : inputValue.trim()
                ? `Няма клиент за „${inputValue.trim()}“`
                : 'Няма клиенти за показване'
          }
          onChange={(_, client) => {
            onChange(client);
            setInputValue(client?.name || '');
          }}
          onInputChange={(_, newInput) => {
            setInputValue(newInput);
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <Box component="li" key={option.id || key} {...rest}>
                <ClientOption client={option} query={inputValue} />
              </Box>
            );
          }}
          ListboxProps={{
            sx: {
              py: 0.75,
              maxHeight: 380,
              '& .MuiAutocomplete-option': {
                alignItems: 'flex-start',
                py: 1,
                px: 1.25,
                mx: 0.75,
                my: 0.25,
                borderRadius: 2,
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Клиент"
              placeholder="Търсене по име, ЕИК, ДДС номер или адрес"
              required={required}
              error={error}
              helperText={
                helperText ||
                (open && options.length > 0
                  ? (searchQuery.trim() ? `${options.length} резултата` : `Последни клиенти · ${options.length}`)
                  : 'Търсене по име, ЕИК, ДДС номер или адрес')
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color={error ? 'error' : 'action'} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setOpen(false);
            setIsAddClientModalOpen(true);
          }}
          disabled={disabled}
          sx={{
            height: 56,
            whiteSpace: 'nowrap',
            px: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Нов клиент
        </Button>
      </Box>

      {value && (
        <Paper
          variant="outlined"
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.04),
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {value.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                {value.eik && <Chip size="small" label={`ЕИК ${value.eik}`} />}
                {value.vatNumber && <Chip size="small" color="primary" variant="outlined" label={`ДДС ${value.vatNumber}`} />}
                {value.clientType === 'B2C' && <Chip size="small" variant="outlined" label="Физическо лице" />}
              </Box>
              {value.address && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {value.address}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {isAddClientModalOpen && (
        <AddClientModalFull
          open={isAddClientModalOpen}
          onClose={() => setIsAddClientModalOpen(false)}
          onClientAdded={handleAdded}
          initialSearchTerm={inputValue}
          companyId={activeCompanyId}
        />
      )}
    </>
  );
};

export default ClientPicker;
